import {
    Button,
    Card, Input, Select, Space, Table,
    Typography,
} from 'antd';
import { Link, useSearchParams } from 'react-router-dom';
import { ClearOutlined, MessageOutlined } from '@ant-design/icons';
import { useAvitoChats } from '../../hooks/useAvitoChats';
import { IAvitoChatsLayout } from '../../types/layouts/avito-chats';

const AvitoChats = ({ params }: { params: IAvitoChatsLayout }) => {
    const {
        addTodayFilter, chats, clearFilters, filters, loading, onFiltersChange, unread,
    } = useAvitoChats(params);
    const [searchParams, setSearchParams] = useSearchParams();

    const onSearch = (val: string) => {
        searchParams.set('search', val);
        searchParams.set('page', '1');
        setSearchParams(searchParams);
    };

    const extra = (
        <Space align="center" size="large" style={{ flex: 1, justifyContent: 'space-between' }}>
            <Space size="large">
                <Typography.Title level={5}>Чаты Авито</Typography.Title>
                <Space>
                    <Select
                        mode="multiple"
                        placeholder="Все аккаунты"
                        options={params.filters.sources}
                        onChange={(values: any) => onFiltersChange('account.orderSource.id', values)}
                        value={filters.find((el) => el.column === 'account.orderSource.id')?.values}
                        style={{ minWidth: 120 }}
                    />
                    <Select
                        allowClear
                        placeholder="Статус сообщения"
                        options={params.filters.statuses}
                        onChange={(value: any) => onFiltersChange('read', typeof value === 'boolean' ? [value] : [])}
                        value={filters.find((el) => el.column === 'read')?.values}
                        style={{ minWidth: 160 }}
                    />
                    <Select
                        placeholder="Тег"
                        mode="multiple"
                        options={params.filters.tags}
                        onChange={(values: any) => onFiltersChange('tags.tag.id', values)}
                        value={filters.find((el) => el.column === 'tags.tag.id')?.values}
                        style={{ minWidth: 120 }}
                    />
                    {unread > 0 && (
                        <Button
                            type="link"
                            onClick={addTodayFilter}
                            disabled={filters.findIndex((el) => el.column === 'last_message_read_at') > -1}
                        >
                            Не прочитано сегодня:
                            {' '}
                            {unread}
                        </Button>
                    )}
                </Space>
            </Space>
            <Space>
                <Button
                    disabled={filters.length === 0}
                    onClick={clearFilters}
                    type={filters.length > 0 ? 'primary' : 'default'}
                    icon={<ClearOutlined />}
                >
                    {filters.length > 0 ? 'Сбросить все фильтры' : ''}
                </Button>
                <Input.Search allowClear placeholder="Поиск..." onSearch={onSearch} value={searchParams.get('search') || undefined} />
            </Space>
        </Space>
    );

    const onPageChange = (p: number, pZ: number) => {
        searchParams.set('page', p.toString());
        searchParams.set('pageSize', pZ.toString());
        setSearchParams(searchParams);
    };

    const onRow = (record: any) => {
        if (record.chats_count > 1) {
            return {
                style: {
                    backgroundColor: '#fff1d8',
                },
            };
        }
        return {};
    };

    return (
        <Card extra={extra}>
            <Table
                bordered
                columns={[{
                    dataIndex: 'read',
                    render: (read) => (!read ? (
                        <div style={{
                            backgroundColor: 'red', borderRadius: 5, height: 10, width: 10,
                        }}
                        />
                    ) : null),
                    width: 10,
                }, {
                    dataIndex: 'order_source',
                    title: 'Детали',
                    render: (os, record: any) => (
                        <Space className="w-100" direction="vertical" size="small">
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <span style={{ backgroundColor: os.color, color: os.text_color, padding: 5 }}>
                                    {os.name}
                                </span>
                                <Button type="link" size="small" onClick={() => onSearch(record.client.url)}>
                                    {`Чаты: ${record.chats_count}`}
                                </Button>
                            </div>
                            <hr style={{ marginRight: -16, marginLeft: -16, opacity: 0.3 }} />
                            <span style={{ color: '#777' }}>
                                {record.last_message_direction === 'out' ? 'Вы:' : 'Клиент:'}
                                {' '}
                                <span style={{ color: '#000', fontWeight: 'bold' }}>{record.last_message_text}</span>
                            </span>
                            <span style={{ color: '#777' }}>{record.last_message_created_at}</span>
                        </Space>
                    ),
                    width: 300,
                }, {
                    dataIndex: 'item_title',
                    title: 'Объявление',
                    render: (_, record: any) => (
                        <a href={record.item_url} target="_blank" rel="noreferrer">{record.item_title}</a>
                    ),
                    width: 300,
                }, {
                    dataIndex: 'note',
                    title: 'Заметка',
                    width: 120,
                }, {
                    dataIndex: 'tags',
                    title: 'Теги',
                    render: (tags) => (
                        <Space direction="horizontal">
                            {tags.map((tag: any) => (
                                <span key={tag.id} style={{ backgroundColor: tag.color, color: tag.text_color, padding: 2 }}>{tag.name}</span>
                            ))}
                        </Space>
                    ),
                    width: 120,
                }, {
                    dataIndex: 'client',
                    title: 'Клиент',
                    render: (_, record: any) => (
                        <a href={record.client.url} target="_blank" rel="noreferrer" style={{ fontWeight: 'bold' }}>{record.client.name}</a>
                    ),
                    width: 120,
                }, {
                    dataIndex: 'phone',
                    title: 'Телефон клиента',
                    render: (_, record: any) => (
                        <span>{record.client.phone}</span>
                    ),
                    width: 120,
                }, {
                    dataIndex: 'sender',
                    title: 'Отправитель последнего сообщения',
                    render: (_, record: any) => (
                        <span>{record.sender?.fio}</span>
                    ),
                    width: 120,
                }, {
                    dataIndex: 'order_id',
                    title: 'Номер заказа',
                    render: (_, record: any) => (
                        <span>{record.order_id}</span>
                    ),
                    width: 120,
                }, {
                    dataIndex: 'client_phone',
                    title: 'Номер телефона',
                    render: (_, record: any) => (
                        <span>{record.client_phone}</span>
                    ),
                    width: 120,
                }, {
                    dataIndex: 'sender',
                    title: 'Отправитель последнего сообщения',
                    render: (_, record: any) => (
                        <span>{record.sender?.fio}</span>
                    ),
                    width: 120,
                }, {
                    dataIndex: 'type',
                    title: 'Тип сообщения',
                    width: 120,
                }, {
                    align: 'center',
                    render: (_, record: any) => (
                        <Link to={`/avito-chats/${record.id}`} target="_blank">
                            <Button type="primary" icon={<MessageOutlined />} />
                        </Link>
                    ),
                    width: 40,
                }]}
                dataSource={chats?.list}
                loading={loading}
                pagination={{
                    current: chats?.currentPage,
                    total: chats?.total,
                    pageSize: chats?.perPage,
                    onChange: onPageChange,
                }}
                onRow={onRow}
            />
        </Card>
    );
};

export default AvitoChats;
